import {
  AllieButton,
  AllieIconButton,
  AllieImg,
  AllieLayoutRight,
  AllieText,
  useOutsideAlerter,
} from '@allie/app-ui-lib';
import { IonList } from '@ionic/react';
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { AuthContext } from '../../context/AuthProvider';
import { config } from '../../helpers/configs';
import { useAllieVersionWatcher, usePushNotifications } from '../../hooks';
import { AppPage, useRoutes } from '../../hooks/useRoutes';
import { Menu } from './components/Menu';
import { GlobalStyle, StyledDesktopMenu } from './Menu.styles';

const MenuDesktopV1 = () => {
  const [expanded, setExpanded] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const { permissions } = useContext(AuthContext);
  const { versions } = useAllieVersionWatcher();
  const { enablePN, silentCheckPermission } = usePushNotifications();
  const [shouldResetAccordions, setShouldResetAccordions] = useState(false);

  useOutsideAlerter(menuRef, () => {
    setExpanded(false);
  });

  silentCheckPermission();

  const routesApp = useRoutes();

  const filterPagesbyPermission = useCallback(
    (pages: AppPage[]): AppPage[] => {
      const finalMenu: AppPage[] = [];

      pages.forEach((menu) => {
        const { children, permissionRequired } = menu;
        if (children) {
          const item = { ...menu };
          item.children = filterPagesbyPermission(children);
          if (item.children.length) finalMenu.push(item);
        } else {
          if (!permissionRequired) finalMenu.push(menu);
          if (permissionRequired) {
            const [subject, action] = permissionRequired;
            if (permissions?.[subject]?.[action]) {
              finalMenu.push(menu);
            }
          }
        }
      });
      return finalMenu;
    },
    [permissions]
  );

  const availablePages = useMemo(
    () => filterPagesbyPermission(routesApp),
    [filterPagesbyPermission, routesApp]
  );

  const showVersions = useMemo(
    () => expanded && config.TEST_ENV && !!Object.keys(versions).length,
    [expanded, config.TEST_ENV, Object.keys(versions).length]
  );

  useEffect(() => {
    if (!expanded) {
      setShouldResetAccordions(true);
    } else {
      setShouldResetAccordions(false);
    }
  }, [expanded]);

  return (
    <>
      <GlobalStyle />
      <StyledDesktopMenu
        className={`main-menu-desktop ${expanded ? 'expanded' : ''}`}
        onClick={() => {
          if (!expanded) {
            setExpanded(true);
          }
        }}
        ref={menuRef}
      >
        <div className="menu-header">
          <div className="logo">
            <AllieImg
              className="logo-img"
              src={config.TEST_ENV ? 'logo-test.png' : 'logo-min.svg'}
            />
            <AllieText type="h2" color="white" weight={300}>
              allie
            </AllieText>
          </div>
          <AllieIconButton
            className="icon-button"
            size="xs"
            onClick={() => {
              if (expanded) {
                setExpanded(false);
              }
            }}
          >
            <AllieLayoutRight />
          </AllieIconButton>
        </div>
        <div className="scrollbar">
          <IonList className="list-menu">
            <Menu
              pages={availablePages.filter((p) => !p.group)}
              shouldResetMenu={shouldResetAccordions}
              expanded={expanded}
            />
          </IonList>
        </div>
        <div
          style={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            zIndex: 99,
            padding: '10px',
          }}
        >
          {/* {expanded && config.TEST_ENV && (
            <AllieButton allieSize="small" onClick={enablePN}>
              {'Enable push'}
            </AllieButton>
          )} */}

          {showVersions && (
            <>
              <AllieText type="body3" weight={600} color="neutral-40">
                {`Native: ${window.screen.width * window.devicePixelRatio}x${
                  window.screen.height * window.devicePixelRatio
                }`}
              </AllieText>
              <AllieText type="body3" weight={600} color="neutral-40">
                {`Absolute: ${window.screen.width}x${window.screen.height}`}
              </AllieText>
              <AllieText type="body3" weight={600} color="neutral-40">
                {`Available: ${window.screen.availWidth}x${window.screen.availHeight}`}
              </AllieText>
              {Object.entries(versions)
                .filter(([_, value]) => !isNaN(Number(value.replace('.', ''))))
                .map(([key, value]) => (
                  <AllieText key={key} type="body2" weight={600} color="neutral-40">
                    {`${key}: v${value}`}
                  </AllieText>
                ))
              }
            </>
          )}
        </div>
      </StyledDesktopMenu>
    </>
  );
};

export default MenuDesktopV1;
